import { Dispatch, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";
import { format } from "date-fns";
import { AppDispatch, RootState } from "../store";

export interface FiscalYear {
  id?: number;
  from: Date;
  to: Date;
  active: boolean;
}

export interface Accounting {
  accounts: any;
  activeFiscalYear: FiscalYear | undefined;
  fiscalYear: FiscalYear | undefined;
  fiscalYears: FiscalYear[];
  incomeStatement: any;
  assets: any;
  liabilities: any;
  ledger: any;
  journal: any;
  voucher: any;
  vouchers: any;
}

export interface AccountingState {
  accounts: any;
  activeFiscalYear: FiscalYear | undefined;
  /** Result of the read-only accounting check. */
  diagnostics: any | undefined;
  diagnosticsStatus: "idle" | "loading" | "failed";
  /**
   * The flat chart of accounts. Distinct from `accounts`, which fetchCoa fills
   * with the grouped statement tree that Coa.tsx renders.
   */
  accountList: any[];
  /** The association's opening position for its earliest fiscal year. */
  openingBalances: any | undefined;
  openingBalancesStatus: "idle" | "loading" | "saving" | "failed";
  fiscalYear: FiscalYear | undefined;
  fiscalYears: FiscalYear[];
  incomeStatement: any;
  assets: any;
  liabilities: any;
  incomeStatementPrev: any[];
  assetsPrev: any[];
  liabilitiesPrev: any[];
  ledger: any;
  journal: any;
  voucher: any;
  vouchers: any[];
  open: boolean;
  status: "idle" | "loading" | "failed";
}

const initialState: AccountingState = {
  accounts: [],
  activeFiscalYear: undefined,
  diagnostics: undefined,
  diagnosticsStatus: "idle",
  accountList: [],
  openingBalances: undefined,
  openingBalancesStatus: "idle",
  fiscalYear: undefined,
  fiscalYears: [],
  incomeStatement: [],
  assets: [],
  liabilities: [],
  incomeStatementPrev: [],
  assetsPrev: [],
  liabilitiesPrev: [],
  ledger: [],
  journal: [],
  voucher: undefined,
  vouchers: [],
  open: false,
  status: "idle",
};

export const fetchActiveFiscalYear = createAsyncThunk(
  "accounting/fetchActiveFiscalYear",
  async (associationId: number) => {
    const response = await axiosInstance(
      `/accounting/fiscal-years/active/${associationId}`
    );
    const data = await response.data;
    return data;
  }
);

export const createFiscalYear = createAsyncThunk(
  "accounting/createFiscalYear",
  async (
    {
      fiscalYear,
      associationId,
    }: { fiscalYear: FiscalYear; associationId: number },
    { dispatch }
  ) => {
    const { data } = await axiosInstance.post(
      `/accounting/fiscal-years?associationId=${associationId}`,
      fiscalYear
    );
    await dispatch(fetchFiscalYears(associationId));
    await dispatch(fetchActiveFiscalYear(associationId));
    return data;
  }
);

export const updateFiscalYear = createAsyncThunk(
  "accounting/updateFiscalYear",
  async (
    {
      id,
      fiscalYear,
      associationId,
    }: { id: number; fiscalYear: FiscalYear | any; associationId: number },
    { dispatch }
  ) => {
    const { data } = await axiosInstance.put(
      `/accounting/fiscal-years/${id}`,
      fiscalYear
    );

    await dispatch(fetchFiscalYears(associationId));
    await dispatch(fetchActiveFiscalYear(associationId));

    return data;
  }
);

export const fetchCoa = createAsyncThunk(
  "accounting/fetchCoa",
  async (associationId: number) => {
    const response = await axiosInstance(
      `/accounting/accounts/coa?associationId=${associationId}`
    );
    const data = await response.data;
    return data;
  }
);

export const createAccount = createAsyncThunk(
  "accounting/createAccount",
  async (
    {
      account,
      associationId,
    }: {
      account: { code: number; name: string; rowId: string };
      associationId: number;
    },
    { dispatch }
  ) => {
    const { data } = await axiosInstance.post(
      `/accounting/accounts?associationId=${associationId}`,
      account
    );
    await dispatch(fetchCoa(associationId));
    return data;
  }
);

export const updateAccount = createAsyncThunk(
  "accounting/updateAccount",
  async (
    {
      id,
      account,
      associationId,
    }: {
      id: number;
      account: { code: number; name: string; rowId: string };
      associationId: number;
    },
    { dispatch }
  ) => {
    const { data } = await axiosInstance.put(
      `/accounting/accounts/${id}?associationId=${associationId}`,
      account
    );
    await dispatch(fetchCoa(associationId));
    return data;
  }
);

export const deleteAccount = createAsyncThunk(
  "accounting/deleteAccount",
  async (
    {
      id,
      associationId,
    }: {
      id: number;
      associationId: number;
    },
    { dispatch }
  ) => {
    const { data } = await axiosInstance.delete(
      `/accounting/accounts/${id}?associationId=${associationId}`
    );
    await dispatch(fetchCoa(associationId));
    return data;
  }
);

export const fetchFiscalYears = createAsyncThunk(
  "accounting/fetchFiscalYears",
  async (associationId: number) => {
    const response = await axiosInstance(
      `/accounting/fiscal-years?associationId=${associationId}`
    );
    const data = await response.data;
    return data;
  }
);

export const fetchFinancialStatements = createAsyncThunk(
  "accounting/fetchFinancialStatements",
  async (params: any) => {
    const { associationId, from, to } = params;
    const response = await axiosInstance(
      `/accounting/reports/financial-statement?associationId=${associationId}&from=${from}&to=${to}`
    );
    const data = await response.data;
    return data;
  }
);

export const fetchFinancialStatementsWithPrev = createAsyncThunk(
  "accounting/fetchFinancialStatementsWithPrev",
  async ({
    associationId,
    from,
    to,
  }: {
    associationId: number;
    from: string | Date;
    to: string | Date;
  }) => {
    const { data } = await axiosInstance.get(
      `/accounting/reports/financial-statement`,
      { params: { associationId, from, to, compare: true } }
    );
    return data;
  }
);

export const fetchDiagnostics = createAsyncThunk(
  "accounting/fetchDiagnostics",
  async (associationId: number) => {
    const { data } = await axiosInstance.get("/accounting/diagnostics", {
      params: { associationId },
    });
    return data;
  }
);

export const fetchAccountList = createAsyncThunk(
  "accounting/fetchAccountList",
  async (associationId: number) => {
    const { data } = await axiosInstance.get("/accounting/accounts", {
      params: { associationId },
    });
    return data;
  }
);

export const fetchOpeningBalances = createAsyncThunk(
  "accounting/fetchOpeningBalances",
  async (params: { associationId: number; fiscalYearId: number }) => {
    const { data } = await axiosInstance.get("/accounting/opening-balances", {
      params,
    });
    return data;
  }
);

export const saveOpeningBalances = createAsyncThunk(
  "accounting/saveOpeningBalances",
  async (
    params: {
      associationId: number;
      fiscalYearId: number;
      balances: { accountCode: number; amount: number }[];
    },
    { dispatch }
  ) => {
    await axiosInstance.put("/accounting/opening-balances", params);
    // Re-read rather than trusting the local values: the server adds the
    // balancing line to 200, and the page shows what was actually booked.
    await dispatch(
      fetchOpeningBalances({
        associationId: params.associationId,
        fiscalYearId: params.fiscalYearId,
      })
    ).unwrap();
  }
);

export const fetchLedger = createAsyncThunk(
  "accounting/fetchLedger",
  async (params: any) => {
    const { associationId, from, to } = params;
    const response = await axiosInstance(
      `/accounting/reports/ledger?associationId=${associationId}&from=${from}&to=${to}`
    );
    const data = await response.data;
    return data;
  }
);

export const fetchJournal = createAsyncThunk(
  "accounting/fetchJournal",
  async (params: any) => {
    const { associationId, from, to } = params;
    const response = await axiosInstance(
      `/accounting/reports/journal?associationId=${associationId}&from=${from}&to=${to}`
    );
    const data = await response.data;
    return data;
  }
);

export const fetchVouchers = createAsyncThunk(
  "accounting/fetchVouchers",
  async (params: any) => {
    const { associationId, from, to } = params;
    const response = await axiosInstance(
      `/accounting/vouchers?associationId=${associationId}&from=${from}&to=${to}`
    );
    const data = await response.data;
    return data;
  }
);

const accountingSlice = createSlice({
  name: "accounting",
  initialState,
  reducers: {
    setFiscalYear(state, action) {
      state.fiscalYear = action.payload;
    },
    addFiscalYears(state, action) {
      const fy: FiscalYear = action.payload;

      if (fy.active) {
        state.fiscalYears = state.fiscalYears.map((f) => ({
          ...f,
          active: false,
        }));
        state.activeFiscalYear = fy;
      }
      state.fiscalYears.push(fy);
    },
    updateFiscalYear(state, action) {
      const updated: FiscalYear = action.payload;
      const idx = state.fiscalYears.findIndex((f) => f.id === updated.id);
      if (idx === -1) return;

      if (updated.active) {
        state.fiscalYears = state.fiscalYears.map((f) =>
          f.id === updated.id ? updated : { ...f, active: false }
        );
        state.activeFiscalYear = updated;
      } else {
        state.fiscalYears[idx] = updated;

        if (state.activeFiscalYear?.id === updated.id && !updated.active) {
          state.activeFiscalYear =
            state.fiscalYears.find((f) => f.active) ?? undefined;
        }
      }
    },

    deleteFiscalYear(state, action) {
      const id = action.payload as number;
      state.fiscalYears = state.fiscalYears.filter((f) => f.id !== id);
      if (state.activeFiscalYear?.id === id) {
        state.activeFiscalYear =
          state.fiscalYears.find((f) => f.active) ?? undefined;
      }
    },
    addVoucher(state, action) {
      state.vouchers.push(action.payload);
    },
    updateVoucher(state, action) {
      const voucherId = state.vouchers.findIndex(
        (u: any) => u.id === action.payload.id
      );
      state.vouchers[voucherId] = action.payload;
    },
    deleteVoucher(state, action) {
      state.vouchers = state.vouchers.filter(
        (u: any) => u.id !== action.payload
      );
    },
    setOpen(state) {
      state.open = !state.open;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCoa.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(fetchAccountList.fulfilled, (state, action) => {
      state.accountList = action.payload;
    });
    builder.addCase(fetchOpeningBalances.pending, (state) => {
      state.openingBalancesStatus = "loading";
    });
    builder.addCase(fetchOpeningBalances.fulfilled, (state, action) => {
      state.openingBalances = action.payload;
      state.openingBalancesStatus = "idle";
    });
    builder.addCase(fetchOpeningBalances.rejected, (state) => {
      state.openingBalancesStatus = "failed";
    });
    builder.addCase(saveOpeningBalances.pending, (state) => {
      state.openingBalancesStatus = "saving";
    });
    builder.addCase(saveOpeningBalances.fulfilled, (state) => {
      state.openingBalancesStatus = "idle";
    });
    builder.addCase(saveOpeningBalances.rejected, (state) => {
      state.openingBalancesStatus = "failed";
    });
    builder.addCase(fetchDiagnostics.pending, (state) => {
      state.diagnosticsStatus = "loading";
    });
    builder.addCase(fetchDiagnostics.fulfilled, (state, action) => {
      state.diagnostics = action.payload;
      state.diagnosticsStatus = "idle";
    });
    builder.addCase(fetchDiagnostics.rejected, (state) => {
      state.diagnosticsStatus = "failed";
    });
    builder.addCase(fetchActiveFiscalYear.fulfilled, (state, action) => {
      state.activeFiscalYear = action.payload;
    });
    builder.addCase(fetchCoa.fulfilled, (state, action) => {
      state.accounts = action.payload;
      state.status = "idle";
    });
    builder.addCase(fetchCoa.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchFiscalYears.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(fetchFiscalYears.fulfilled, (state, action) => {
      state.fiscalYears = action.payload;
      state.status = "idle";
    });
    builder.addCase(fetchFiscalYears.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchFinancialStatements.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(fetchFinancialStatements.fulfilled, (state, action) => {
      state.incomeStatement = action.payload.incomeStatement;
      state.assets = action.payload.assets;
      state.liabilities = action.payload.liabilities;
      state.status = "idle";
    });
    builder.addCase(fetchFinancialStatements.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(
      fetchFinancialStatementsWithPrev.fulfilled,
      (state, action) => {
        const { current, previous } = action.payload;
        state.incomeStatement = current.incomeStatement;
        state.assets = current.assets;
        state.liabilities = current.liabilities;

        state.incomeStatementPrev = previous?.incomeStatement ?? [];
        state.assetsPrev = previous?.assets ?? [];
        state.liabilitiesPrev = previous?.liabilities ?? [];
        state.status = "idle";
      }
    );
    builder.addCase(fetchLedger.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(fetchLedger.fulfilled, (state, action) => {
      state.ledger = action.payload;
      state.status = "idle";
    });
    builder.addCase(fetchLedger.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchJournal.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(fetchJournal.fulfilled, (state, action) => {
      state.journal = action.payload;
      state.status = "idle";
    });
    builder.addCase(fetchJournal.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchVouchers.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(fetchVouchers.fulfilled, (state, action) => {
      state.vouchers = action.payload;
      state.status = "idle";
    });
    builder.addCase(fetchVouchers.rejected, (state) => {
      state.status = "failed";
    });
  },
});

export function setFiscalYear(fiscalYear: FiscalYear) {
  return async (dispatch: Dispatch) => {
    dispatch(accountingSlice.actions.setFiscalYear(fiscalYear));
  };
}

export function deleteFiscalYear(id: number) {
  return async (dispatch: Dispatch) => {
    await axiosInstance.delete(`/accounting/fiscal-years/${id}`);
    dispatch(accountingSlice.actions.deleteFiscalYear(id));
  };
}

export function createVoucher(voucher: any, associationId: number) {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    await axiosInstance.post(
      `/accounting/vouchers?associationId=${associationId}`,
      voucher
    );

    const { accounting } = getState();
    const fy = accounting.activeFiscalYear;
    if (fy) {
      const from = format(new Date(fy.from), "yyyy-MM-dd");
      const to = format(new Date(fy.to), "yyyy-MM-dd");
      await dispatch(fetchVouchers({ associationId, from, to }));
    }
  };
}

export function updateVoucher(id: number, voucher: any) {
  return async (dispatch: Dispatch) => {
    const response = await axiosInstance.put(
      `/accounting/vouchers/${id}`,
      voucher
    );
    dispatch(accountingSlice.actions.updateVoucher(response.data));
  };
}

export function deleteVoucher(id: number) {
  return async (dispatch: Dispatch) => {
    await axiosInstance.delete(`/accounting/vouchers/${id}`);
    dispatch(accountingSlice.actions.deleteVoucher(id));
  };
}

export function openFiscalYearsForm() {
  return async (dispatch: Dispatch) => {
    dispatch(accountingSlice.actions.setOpen());
  };
}

export default accountingSlice.reducer;
